import React, { useState, useEffect, useRef } from 'react';
import { getOverdueCount } from '../api/services/feesService';
import { useNavigate } from 'react-router-dom';
import dingoIcon from '../assets/img/logo/dingo-icon.png';
import dingoLogo from '../assets/img/logo/dingo-logo.png';
import { Avatar } from './ui/dg';
import { useAuth } from '../contexts/AuthContext';
import { canOpenSection, roleLabel } from '../lib/permissions';
import { getAllTickets } from '../api/services/ticketsService';
import { getAllVerifications } from '../api/services/verificationsService';
import { getCampaigns } from '../api/services/marketingService';

/**
 * Barre latérale dessinée comme le drawer de l'app (docs/backoffice) : le logo
 * en tête, les sections sous deux en-têtes en petites capitales, l'entrée
 * courante sur un fond indigo léger, des compteurs en pastilles. Le compte
 * (avec son menu : profil, paramètres, déconnexion) reste en bas, une roue
 * dentée à côté mène aux paramètres.
 * Repliée, elle devient un rail d'icônes : un point remplace le compteur et
 * une bulle donne le nom au survol. La recherche vit dans l'en-tête (⌘K).
 */

// Icônes — trait 1,5 sur 20 px : elles accompagnent le libellé, elles ne
// l'illustrent pas.
type IconProps = { className?: string; size?: number };
const Icon: React.FC<IconProps & { children: React.ReactNode }> = ({ className, size = 20, children }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const HomeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
    <path d="M9.5 21v-7h5v7" />
  </Icon>
);
const KeysIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15 7a2 2 0 0 1 2 2m4 0a6 6 0 0 1-7.743 5.743L11 17H9v2H7v2H4a1 1 0 0 1-1-1v-2.586a1 1 0 0 1 .293-.707l5.964-5.964A6 6 0 1 1 21 9z" />
  </Icon>
);
const RentalsIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 16v-5l2-5h10l2 5v5z" />
    <path d="M5 16v2M19 16v2" />
    <circle cx="8" cy="13" r="1" />
    <circle cx="16" cy="13" r="1" />
  </Icon>
);
const DocumentIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8z" />
    <path d="M14 3v5h5" />
    <path d="M8.5 13h7M8.5 17h7" />
  </Icon>
);
const SupportIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="M5.6 5.6l3.9 3.9m5 5l3.9 3.9m0-12.8l-3.9 3.9m-5 5l-3.9 3.9" />
  </Icon>
);
const UserIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M19.5 21v-1.5a4 4 0 0 0-4-4h-7a4 4 0 0 0-4 4V21" />
    <circle cx="12" cy="7.5" r="4" />
  </Icon>
);
const AnalyticsIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20.5 15.5A9 9 0 1 1 8.5 3.5" />
    <path d="M21 12a9 9 0 0 0-9-9v9z" />
  </Icon>
);
const FeesIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6.5 9.5v5M17.5 9.5v5" />
  </Icon>
);
const TicketIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4z" />
    <path d="M14 6v12" strokeDasharray="2 2" />
  </Icon>
);

const MegaphoneIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 10.5v3a1.5 1.5 0 0 0 1.5 1.5H8l6 4V5l-6 4H5.5A1.5 1.5 0 0 0 4 10.5z" />
    <path d="M17.5 9.5a3.5 3.5 0 0 1 0 5" />
    <path d="M8 15v4" />
  </Icon>
);
const JournalIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5z" />
    <path d="M8 9h8M8 12.5h8M8 16h5" />
  </Icon>
);
const SettingsIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Icon>
);
const LogoutIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </Icon>
);
const CloseIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);
// Deux chevrons superposés : la poignée d'un sélecteur, pas d'un dépliant.
const ChevronUpDownIcon = (p: IconProps) => (
  <Icon size={16} {...p}>
    <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
  </Icon>
);
// La bascule dessine ce qu'elle fait : un cadre avec un panneau à gauche.
const PanelIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M9 4v16" />
  </Icon>
);

const COLLAPSED_KEY = 'dingo.sidebar.collapsed';

/** Le rail n'existe qu'au-dessus du point de rupture `lg` de Tailwind. */
function useIsDesktop(): boolean {
  const query = '(min-width: 1024px)';
  const [desktop, setDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : true
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return desktop;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Stockage indisponible (navigation privée, quota) : on perd juste la
    // mémoire de la barre, pas la navigation.
  }
}

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  activeSection: string;
  setActiveSection: (section: string) => void;
  onSectionChange?: (section: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  activeSection,
  setActiveSection,
  onSectionChange
}) => {
  const { user, token, logout } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [pendingVerificationsCount, setPendingVerificationsCount] = useState(0);
  const [pendingCampaignsCount, setPendingCampaignsCount] = useState(0);
  const [overdueOwnersCount, setOverdueOwnersCount] = useState(0);

  useEffect(() => {
    if (!isStaff || !token) return;
    getAllTickets(token)
      .then((data: any) => setOpenTicketsCount(Array.isArray(data) ? data.filter((t: any) => t.status === 'open').length : 0))
      .catch(() => {});
    getAllVerifications(token)
      .then((data: any) => setPendingVerificationsCount(Array.isArray(data) ? data.filter((v: any) => v.status === 'pending').length : 0))
      .catch(() => {});
    // Propriétaires en retard de règlement (administrateurs, comme la section).
    if (canOpenSection(user?.role, 'frais')) {
      getOverdueCount(token).then(setOverdueOwnersCount).catch(() => {});
    }
    // Réservé aux administrateurs côté API : un modérateur recevrait un 403,
    // avalé ici comme les autres échecs.
    if (canOpenSection(user?.role, 'communication')) {
      getCampaigns(token, 'pending')
        .then((rows) => setPendingCampaignsCount(Array.isArray(rows) ? rows.length : 0))
        .catch(() => {});
    }
  }, [isStaff, token, user?.role]);
  const navigate = useNavigate();

  const isDesktop = useIsDesktop();
  // Le repli est un réglage qu'on prend une fois pour toutes : il survit au
  // rechargement. Sur mobile la barre est un tiroir, jamais un rail.
  const [collapsed, setCollapsed] = useState<boolean>(() => readJson<boolean>(COLLAPSED_KEY, false));
  useEffect(() => { writeJson(COLLAPSED_KEY, collapsed); }, [collapsed]);
  const rail = collapsed && isDesktop;

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  // Bulle du rail : le nom (et le compteur) de l'icône survolée. En position
  // fixe, pour sortir de la zone qui défile.
  const [tip, setTip] = useState<{ label: string; badge?: number; amber?: boolean; top: number; left: number } | null>(null);
  useEffect(() => { if (!rail) setTip(null); }, [rail]);
  const accountRef = useRef<HTMLDivElement>(null);

  // Un clic ailleurs ou Échap referme le menu du compte.
  useEffect(() => {
    if (!accountMenuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setAccountMenuOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountMenuOpen]);

  // Les compteurs : indigo pour ce qui est à traiter, ambre pour l'argent en retard.
  const allNavigationItems: { id: string; label: string; icon: React.ReactElement; badge?: number; badgeTone?: 'amber' }[] = [
    { id: 'dashboard', label: 'Tableau de bord', icon: <HomeIcon /> },
    { id: 'mytrips', label: 'Trajets', icon: <KeysIcon /> },
    { id: 'myrentals', label: 'Flotte', icon: <RentalsIcon /> },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 'frais', label: 'Frais', icon: <FeesIcon />, badge: overdueOwnersCount || undefined, badgeTone: 'amber' },
    { id: 'promo-codes', label: 'Codes promo', icon: <TicketIcon /> },
    { id: 'communication', label: 'Communication', icon: <MegaphoneIcon />, badge: pendingCampaignsCount || undefined },
    { id: 'support', label: 'Support', icon: <SupportIcon />, badge: openTicketsCount || undefined },
    { id: 'users', label: 'Utilisateurs', icon: <UserIcon /> },
    { id: 'verifications', label: 'Vérifications', icon: <DocumentIcon />, badge: pendingVerificationsCount || undefined },
    { id: 'journaux', label: 'Journaux', icon: <JournalIcon /> },
  ];

  // Ce qu'on ne peut pas ouvrir, on ne le montre pas.
  const navigationItems = allNavigationItems.filter((item) => canOpenSection(user?.role, item.id));

  /**
   * Le tableau de bord seul en tête, puis deux familles : ce qu'on fait tous
   * les jours, et ce qui engage la plateforme.
   */
  const NAVIGATION_GROUPS: { label: string; ids: string[] }[] = [
    { label: 'Exploitation', ids: ['mytrips', 'myrentals', 'verifications', 'support'] },
    { label: 'Administration', ids: ['users', 'analytics', 'frais', 'promo-codes', 'communication', 'journaux'] }
  ];

  const groupedNavigation = NAVIGATION_GROUPS
    .map((group) => ({
      label: group.label,
      items: group.ids
        .map((id) => navigationItems.find((item) => item.id === id))
        .filter((item): item is (typeof navigationItems)[number] => Boolean(item))
    }))
    .filter((group) => group.items.length > 0);

  const homeItem = navigationItems.find((item) => item.id === 'dashboard');

  const goTo = (section: string) => {
    setActiveSection(section);
    onSectionChange?.(section);
    navigate(`/${section}`);
    setSidebarOpen(false);
  };

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.email || 'Compte';

  /** Une ligne de navigation : icône seule dans le rail, icône et libellé sinon. */
  const NavRow = ({
    icon, label, active, onClick, badge, badgeTone
  }: {
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    onClick: () => void;
    badge?: number;
    badgeTone?: 'amber';
  }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={rail ? label : undefined}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={rail ? (e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ label, badge, amber: badgeTone === 'amber', top: r.top + r.height / 2, left: r.right + 10 }); } : undefined}
      onMouseLeave={rail ? () => setTip(null) : undefined}
      className={`group flex w-full items-center rounded-xl text-[14px] transition-colors ${
        rail ? 'mx-auto h-11 w-12 justify-center' : 'h-10 gap-3 px-3'
      } ${
        active ? 'bg-[#EFEEFD] font-bold text-[#4F46E5]' : 'font-medium text-[#1E293B] hover:bg-[#F7F7FB]'
      }`}
    >
      <span className="relative shrink-0">
        {icon}
        {rail && badge ? (
          <span className={`absolute -right-1 -top-1 h-2 w-2 rounded-full ring-2 ${active ? 'ring-[#EFEEFD]' : 'ring-white'} ${badgeTone === 'amber' ? 'bg-[#F59E0B]' : 'bg-[#4F46E5]'}`} />
        ) : null}
      </span>
      {!rail && <span className="min-w-0 flex-1 truncate text-left">{label}</span>}
      {!rail && badge ? (
        <span className={`flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
          badgeTone === 'amber' ? 'bg-[#FFF4DB] text-[#B45309]' : 'bg-[#4F46E5] text-white'
        }`}>{badge}</span>
      ) : null}
    </button>
  );

  return (
    <>
      {/* Voile pour mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        aria-label="Navigation principale"
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col border-r border-[#EEF0F4] bg-white text-[#1E293B] transition-all duration-200 ease-out ${
          rail ? 'w-[72px]' : 'w-[248px]'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo ; la bascule du rail à droite */}
        <div className={`flex h-[76px] shrink-0 items-center ${rail ? 'justify-center' : 'gap-2.5 pl-6 pr-3'}`}>
          <button type="button" onClick={() => goTo('dashboard')} title="Tableau de bord" className="shrink-0 rounded-lg">
            {rail ? <img src={dingoIcon} alt="Dingo" className="w-8" /> : <img src={dingoLogo} alt="Dingo" className="h-[30px]" />}
          </button>
          {!rail && (
            <>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                title="Réduire la barre latérale"
                aria-label="Réduire la barre latérale"
                className="hidden h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-[#F7F7FB] hover:text-[#1E293B] lg:flex"
              >
                <PanelIcon size={18} />
              </button>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F7F7FB] lg:hidden"
                aria-label="Fermer la navigation"
              >
                <CloseIcon size={18} />
              </button>
            </>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3">
          {rail && (
            <div className="mb-2 flex justify-center">
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                title="Déplier la barre latérale"
                aria-label="Déplier la barre latérale"
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-[#F7F7FB] hover:text-[#1E293B]"
              >
                <PanelIcon size={18} />
              </button>
            </div>
          )}
          {homeItem && (
            <NavRow icon={homeItem.icon} label={homeItem.label} active={activeSection === homeItem.id} onClick={() => goTo(homeItem.id)} />
          )}
          {groupedNavigation.map((group) => (
            <div key={group.label}>
              {rail ? <div className="mx-2 my-2 border-t border-[#EEF0F4]" /> : (
                <p className="px-3 pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">{group.label}</p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavRow
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    active={activeSection === item.id}
                    onClick={() => goTo(item.id)}
                    badge={item.badge}
                    badgeTone={item.badgeTone}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Le compte et son menu, la roue dentée des paramètres à côté */}
        <div className="shrink-0 px-3 pb-4">
          <div ref={accountRef} className="relative flex items-center border-t border-[#EEF0F4] pt-2">
            <button
              type="button"
              onClick={() => setAccountMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              aria-label={rail ? displayName : undefined}
              className={`flex w-full items-center rounded-[14px] py-2 text-left transition-colors hover:bg-[#F7F7FB] ${accountMenuOpen ? 'bg-[#F7F7FB]' : ''} ${rail ? 'justify-center' : 'gap-2.5 pl-2.5 pr-1'}`}
            >
              <Avatar name={displayName} photo={(user as any)?.profile_photo} size={36} />
              {!rail && (
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-bold text-[#1E293B]">{displayName}</span>
                  <span className="block truncate text-[12px] text-[#6B7280]">{roleLabel(user?.role)}</span>
                </span>
              )}
              {!rail && <ChevronUpDownIcon className="shrink-0 text-[#9CA3AF]" />}
            </button>
            {!rail && (
              <button
                type="button"
                onClick={() => { setAccountMenuOpen(false); goTo('settings'); }}
                aria-label="Paramètres"
                title="Paramètres"
                className={`flex h-8 w-6 shrink-0 items-center justify-center transition-colors ${
                  activeSection === 'settings' ? 'text-[#4F46E5]' : 'text-[#9CA3AF] hover:text-[#1E293B]'
                }`}
              >
                <SettingsIcon size={16} />
              </button>
            )}
            {accountMenuOpen && (
              <div
                role="menu"
                className={`absolute bottom-full z-20 mb-2 overflow-hidden rounded-[18px] border border-[#EEF0F4] bg-white p-1.5 shadow-[0_12px_32px_rgba(15,23,42,0.16)] ${
                  rail ? 'left-full ml-2 w-64' : 'left-0 right-0'
                }`}
              >
                <div className="mb-1 flex items-center gap-2.5 border-b border-[#EEF0F4] px-2.5 py-3">
                  <Avatar name={displayName} photo={(user as any)?.profile_photo} size={40} />
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-bold text-[#1E293B]">{displayName}</span>
                    <span className="block truncate text-[12.5px] text-[#6B7280]">{user?.email}</span>
                  </span>
                </div>
                {[
                  { id: 'user', label: 'Mon profil', icon: <UserIcon size={18} /> },
                  { id: 'settings', label: 'Paramètres', icon: <SettingsIcon size={18} /> }
                ].map((item) => (
                  <button key={item.id} type="button" role="menuitem"
                    onClick={() => { setAccountMenuOpen(false); goTo(item.id); }}
                    className="flex h-11 w-full items-center gap-3 rounded-xl px-2.5 text-left text-[14px] font-semibold text-[#1E293B] hover:bg-[#F7F7FB]">
                    <span className="text-[#475569]">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
                <div className="my-1 border-t border-[#EEF0F4]" />
                <button type="button" role="menuitem"
                  onClick={() => { setAccountMenuOpen(false); logout(); }}
                  className="flex h-11 w-full items-center gap-3 rounded-xl px-2.5 text-left text-[14px] font-semibold text-[#B91C1C] hover:bg-[#FDECEF]">
                  <LogoutIcon size={18} />
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {rail && tip && (
        <div role="tooltip" className="pointer-events-none fixed z-[60] flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-[10px] bg-[#1E293B] px-3 py-2 text-[13px] font-semibold text-white shadow-[0_6px_18px_rgba(15,23,42,0.18)]"
          style={{ top: tip.top, left: tip.left }}>
          <span className="absolute -left-[5px] top-1/2 h-2.5 w-2.5 -translate-y-1/2 rotate-45 bg-[#1E293B]" />
          {tip.label}
          {tip.badge ? <span className={`rounded-full px-[7px] ${tip.amber ? 'bg-[#F59E0B]' : 'bg-[#4F46E5]'} text-[11px] font-bold leading-[18px]`}>{tip.badge}</span> : null}
        </div>
      )}
    </>
  );
};

export default Sidebar;
